import { FileButtonInput } from "@marketplace/shared/ui";
import {
  Anchor,
  Button,
  Input,
  Loader,
  LoadingOverlay,
  Modal,
  NumberInput,
  Select,
  Text,
  TextInput
} from "@marketplace/ui";
import { DatePickerInput } from "@marketplace/ui/dates";
import { notifications } from "@marketplace/ui/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IoMdEye, IoMdEyeOff } from "react-icons/io";
import { MdCheckCircle } from "react-icons/md";

import { BASE_MEDIA_URL } from "@/constants";

import { useGetBankAccountListQuery } from "../../../settings/hooks/queries/use-get-bank-account-list-query";
import { useGetBankAccountRevealDetailQuery } from "../../../settings/hooks/queries/use-get-bank-account-reveal-detail-query";
import { claimOrderSubmitModalAtom } from "../../atoms/claim-order-submit-modal-atom";
import { useClaimOrderSubmitForm } from "../../hooks/forms/use-claim-order-submit-form";
import { ClaimEstimationBreakdown } from "../claim-estimation-breakdown";
import { ClaimOrderStatusTracker } from "../claim-order-status-tracker";

type ClaimOrderSubmitModalProps = {
  onSuccess?: () => void;
};

export const ClaimOrderSubmitModal = ({
  onSuccess
}: ClaimOrderSubmitModalProps) => {
  const { t } = useTranslation("order", {
    keyPrefix: "claimOrderPage.submitModal"
  });

  const [modal] = useAtom(claimOrderSubmitModalAtom);
  const [masked, setMasked] = useState(true);
  const queryClient = useQueryClient();

  const { data: bankAccountData } = useGetBankAccountListQuery({
    options: {
      enabled: modal.visible
    }
  });

  const handleToggleMask = () => {
    setMasked((prev) => !prev);
  };

  const {
    formMethods,
    showConfirm,
    setShowConfirm,
    isModalVisible,
    orders,
    estimation,
    estimationData,
    isEstimationLoading,
    handleSubmit,
    handleConfirmSubmit,
    handleClose,
    handleInvoiceFileOnChange,
    handleDeleteInvoiceFile,
    isPending,
    isPresignedLoading
  } = useClaimOrderSubmitForm({
    onSuccess,
    bankAccountData: bankAccountData?.data
  });

  const {
    register,
    control,
    watch,
    formState: { errors, isValid }
  } = formMethods;

  const watchedInvoiceAmount = watch("invoiceAmount");
  const invoiceAmountNum = watchedInvoiceAmount
    ? Number(watchedInvoiceAmount)
    : 0;
  const selectedBankAccountId = watch("bankAccountId");

  const {
    data: revealedBankAccount,
    isFetching: isRevealing,
    isError: isRevealError
  } = useGetBankAccountRevealDetailQuery({
    bankInfoId: String(selectedBankAccountId ?? ""),
    options: {
      enabled: !masked && modal.visible && !!selectedBankAccountId
    }
  });

  useEffect(() => {
    if (!masked && isRevealError) {
      notifications.show({
        variant: "error",
        message: t("revealFailed") as string
      });
      setMasked(true);
    }
  }, [masked, isRevealError, t]);

  useEffect(() => {
    if (!modal.visible) {
      setMasked(true);
      queryClient.removeQueries({ queryKey: ["bankAccountRevealDetail"] });
    }
  }, [modal.visible, queryClient]);

  const revealedAccountNumber = revealedBankAccount?.data?.account_number;
  const revealedAccountName = revealedBankAccount?.data?.account_name;

  const bankAccountOptions =
    bankAccountData?.data?.map((bank) => {
      const isSelected =
        String(bank.id) === String(selectedBankAccountId ?? "");
      const accountNumber =
        !masked && isSelected
          ? (revealedAccountNumber ?? bank.account_number)
          : bank.account_number;
      const accountName =
        !masked && isSelected
          ? (revealedAccountName ?? bank.account_name)
          : bank.account_name;

      return {
        value: String(bank.id),
        label: `${accountName} - ${accountNumber} (${bank.bank_name})`
      };
    }) ?? [];

  return (
    <>
      <Modal
        opened={isModalVisible && !showConfirm}
        onClose={handleClose}
        title={
          <Text type="heading" className="text-fjord-grey-500">
            {t("title")}
          </Text>
        }
        size={610}
        withCloseButton={false}
        classNames={{ body: "!pt-0" }}
      >
        <div className="space-y-6">
          {modal.publicId && (
            <ClaimOrderStatusTracker
              publicId={modal.publicId}
              rejectReason={modal.prefill?.rejectReason}
              currentStatus={modal.currentStatus}
            />
          )}
          <div className="space-y-1">
            <Text className="text-fjord-grey-500">{t("orderList")}</Text>
            <div className="rounded-lg bg-mischka-100 border border-solid border-mischka-400 p-3 space-y-[14px]">
              {orders.map((order) => (
                <div
                  key={order.order_id}
                  className="grid grid-cols-[auto_1fr_1fr] items-center gap-2"
                >
                  <MdCheckCircle
                    className="text-noble-blue-500 shrink-0"
                    size={16}
                  />
                  <Text className="text-fjord-grey-500">{order.order_id}</Text>
                  <Text className="text-fjord-grey-500">
                    {order.buyer_company_name}
                  </Text>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <TextInput
                label={t("invoiceNumber")}
                placeholder={t("invoiceNumberPlaceholder")}
                required
                size="lg"
                error={errors.invoiceNumber?.message}
                {...register("invoiceNumber")}
              />
            </div>
            <div className="flex-1">
              <Controller
                name="invoiceDate"
                control={control}
                render={({ field }) => (
                  <DatePickerInput
                    {...field}
                    label={t("invoiceDate")}
                    placeholder={t("invoiceDatePlaceholder")}
                    required
                    size="lg"
                    clearable={false}
                    valueFormat="DD/MM/YYYY"
                    value={field.value}
                    onChange={(value) =>
                      field.onChange(new Date(value as string))
                    }
                    error={errors.invoiceDate?.message}
                  />
                )}
              />
            </div>
          </div>

          <TextInput
            label={t("taxInvoiceNumber")}
            placeholder={t("taxInvoiceNumberPlaceholder")}
            required
            size="lg"
            error={errors.taxInvoiceNumber?.message}
            {...register("taxInvoiceNumber")}
          />

          <Input.Wrapper
            label={t("invoiceFile")}
            error={errors.invoiceFile?.message}
            withAsterisk
          >
            <Controller
              name="invoiceFile"
              control={control}
              render={({ field: { value } }) => (
                <div className="flex gap-x-2 items-center mt-1">
                  <FileButtonInput
                    fileValue={value instanceof File ? value : null}
                    onChange={handleInvoiceFileOnChange}
                    deleteFile={handleDeleteInvoiceFile}
                    loading={isPresignedLoading}
                    accept="application/pdf"
                  />
                  {isPresignedLoading ? (
                    <Loader size="sm" />
                  ) : value instanceof File ? (
                    <Text
                      type="paragraph"
                      size="s"
                      className="text-ellipsis overflow-hidden whitespace-nowrap max-w-full"
                    >
                      {value.name}
                    </Text>
                  ) : modal.prefill?.invoiceFilePath ? (
                    <Anchor
                      href={`${BASE_MEDIA_URL}/${modal.prefill.invoiceFilePath}`}
                      target="_blank"
                      className="text-ellipsis block overflow-hidden whitespace-nowrap max-w-full"
                    >
                      {modal.prefill.invoiceFilePath.split("/").pop()}
                    </Anchor>
                  ) : (
                    <Text
                      type="paragraph"
                      size="s"
                      className="text-fjord-grey-500 line-clamp-1"
                    >
                      {t("invoiceFileHint")}
                    </Text>
                  )}
                </div>
              )}
            />
          </Input.Wrapper>

          <Controller
            name="invoiceAmount"
            control={control}
            render={({ field: { value, onChange } }) => (
              <NumberInput
                label={t("invoiceAmount")}
                required
                size="lg"
                placeholder="0"
                value={value}
                onChange={onChange}
                error={errors.invoiceAmount?.message}
                min={1}
                max={9_999_999_999}
                clampBehavior="strict"
                hideControls
                allowDecimal={false}
                thousandSeparator="."
                decimalSeparator=","
                disabled
              />
            )}
          />

          <ClaimEstimationBreakdown
            invoiceAmount={invoiceAmountNum}
            estimation={estimation}
            isEstimationLoading={isEstimationLoading}
            totalProductPrice={estimationData?.total_product_price}
            totalPpn={estimationData?.total_ppn}
            totalPph23={estimationData?.total_pph23}
            payoutFee={estimationData?.payout_fee}
          />
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <Controller
                name="bankAccountId"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    label={t("bankAccount")}
                    required
                    size="lg"
                    placeholder={t("bankAccountPlaceholder")}
                    data={bankAccountOptions}
                    value={value}
                    onChange={onChange}
                    error={errors.bankAccountId?.message}
                  />
                )}
              />
            </div>
            <div className="mt-6">
              {!masked && isRevealing && !!selectedBankAccountId ? (
                <Loader size="sm" />
              ) : masked ? (
                <IoMdEye
                  size={20}
                  className="cursor-pointer text-fjord-grey-500"
                  onClick={handleToggleMask}
                />
              ) : (
                <IoMdEyeOff
                  size={20}
                  className="cursor-pointer text-fjord-grey-500"
                  onClick={handleToggleMask}
                />
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="secondary"
              onClick={handleClose}
              className="rounded-full px-6"
            >
              {t("cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!isValid}
              className="rounded-full px-6"
            >
              {t("submit")}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        opened={showConfirm}
        onClose={() => setShowConfirm(false)}
        title={
          <Text type="heading" size="l" className="text-fjord-grey-500">
            {t("confirmModal.title")}
          </Text>
        }
        size={440}
        withCloseButton={false}
        classNames={{ body: "!pt-0" }}
      >
        <LoadingOverlay visible={isPending} />
        <Text className="text-fjord-grey-400 mb-8">
          {t("confirmModal.description")}
        </Text>
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={() => setShowConfirm(false)}
            className="rounded-full px-6"
            disabled={isPending}
          >
            {t("confirmModal.goBack")}
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmSubmit}
            loading={isPending}
            className="rounded-full px-6"
          >
            {t("confirmModal.submit")}
          </Button>
        </div>
      </Modal>
    </>
  );
};
